import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SoftBadge from 'components/common/SoftBadge';
import { getBadge, timeAgo, TYPE_GUIDANCE } from './alertMeta';

/**
 * Every completion and reopen, oldest first. Shown on open alerts too: one that
 * was completed and then reopened has history worth seeing while it's still
 * outstanding.
 */
export const AuditTrail = ({ actions }) => {
  const list = Array.isArray(actions) ? actions : [];
  if (!list.length) return null;

  return (
    <div className="mt-2 border-start border-3 border-300 ps-2">
      {list.map((entry, i) => (
        <div key={i} className={i > 0 ? 'mt-1' : undefined}>
          <div className="text-600 fs--1">
            <span
              className={
                entry.action === 'completed'
                  ? 'text-success fw-semi-bold'
                  : 'text-warning fw-semi-bold'
              }
            >
              {entry.action === 'completed' ? 'Completed' : 'Reopened'}
            </span>{' '}
            by <span className="fw-semi-bold">{entry.by || 'a colleague'}</span>
            {entry.at && ` ${timeAgo(entry.at)}`}
          </div>
          {entry.notes && (
            <div className="text-500 fs--1" style={{ whiteSpace: 'normal' }}>
              “{entry.notes}”
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

AuditTrail.propTypes = { actions: PropTypes.array };

/**
 * One alert row. Used inside the bell dropdown and on the Alerts page.
 */
const AlertCard = ({
  notification,
  completed = false,
  onOpen,
  onComplete,
  onReopen,
  className = ''
}) => {
  const badge = getBadge(notification.type);
  const guidance = TYPE_GUIDANCE[notification.type];

  return (
    <div
      className={`px-3 py-3 border-bottom ${className}`}
      role="button"
      onClick={() => onOpen?.(notification)}
    >
      <div className="d-flex justify-content-between align-items-start">
        <div className="flex-1 me-2">
          <div className="fw-semi-bold text-800 mb-1 fs-0">
            {notification.title}
          </div>
          {notification.body && (
            <p
              className="mb-1 text-600 fs--1"
              style={{ whiteSpace: 'normal', lineHeight: '1.5' }}
            >
              {notification.body}
            </p>
          )}
          {guidance && !completed && (
            <p
              className="mb-2 text-700 fs--1 fst-italic"
              style={{ whiteSpace: 'normal' }}
            >
              <FontAwesomeIcon icon="info-circle" className="me-1" />
              {guidance}
            </p>
          )}
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <SoftBadge bg={badge.bg} pill className="fs--1">
              {badge.label}
            </SoftBadge>
            <span className="text-500 fs--1">
              {timeAgo(notification.created_at)}
            </span>
          </div>
          <AuditTrail actions={notification.actions} />
        </div>
        <div className="d-flex align-items-center gap-2 flex-shrink-0">
          {!completed && !notification.is_read && (
            <span
              className="bg-primary rounded-circle d-inline-block"
              style={{ width: '8px', height: '8px' }}
              title="Unread"
            />
          )}
          {completed ? (
            <Button
              variant="link"
              size="sm"
              className="p-0 text-600 fs--1"
              title="Reopen this alert"
              onClick={e => {
                e.stopPropagation();
                onReopen?.(notification);
              }}
            >
              Reopen
            </Button>
          ) : (
            <Button
              variant="falcon-success"
              size="sm"
              title="Mark as completed"
              onClick={e => {
                e.stopPropagation();
                onComplete?.(notification);
              }}
            >
              <FontAwesomeIcon icon="check" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

AlertCard.propTypes = {
  notification: PropTypes.object.isRequired,
  completed: PropTypes.bool,
  onOpen: PropTypes.func,
  onComplete: PropTypes.func,
  onReopen: PropTypes.func,
  className: PropTypes.string
};

export default AlertCard;
